import { type PropsWithChildren, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { AccountHeader } from "@/components/Layout/account/AccountHeader";
import { AccountSidebar } from "@/components/Layout/account/AccountSidebar";
import { InterfaceBuilderInspector } from "@/components/Layout/account/InterfaceBuilderInspector";
import { InterfaceBuilderToolbar } from "@/components/Layout/account/InterfaceBuilderToolbar";
import { useUser } from "@/contexts/UserContext";
import { useMediaQueryEffect } from "@/hooks/useMediaQueryEffect";
import { useInterfaceBuilderStorageSync } from "@/hooks/useInterfaceBuilderStorageSync";
import "./AccountLayout.css";

export type AccountSidebarState = "expanded" | "collapsed";

// The sidebar and the workspace beside it do not both fit on a narrow
// viewport: at 390px the 264px sidebar left the pages 126px to draw a table
// in, so every column came out a sliver. The sidebar opens as its icon rail
// there instead, and goes back to its full width when there is room again.
const NARROW_LAYOUT_MEDIA_QUERY = "(max-width: 900px)";

function readInitialSidebarState(): AccountSidebarState {
  if (typeof window === "undefined" || !window.matchMedia) return "expanded";
  return window.matchMedia(NARROW_LAYOUT_MEDIA_QUERY).matches
    ? "collapsed"
    : "expanded";
}

export function AccountLayout({ children }: PropsWithChildren) {
  const { t } = useTranslation();
  const [sidebarState, setSidebarState] = useState<AccountSidebarState>(
    readInitialSidebarState,
  );
  const {
    isInterfaceBuilderInspectorOpen,
    isInterfaceBuilderMode,
    session,
  } = useUser();

  useInterfaceBuilderStorageSync({
    isAuthenticated: session.isAuthenticated,
    userId: session.user?.uuid,
    projectId: session.projectId,
    permissions: session.permissions,
  });

  const toggleSidebar = () => {
    setSidebarState((currentState) => (
      currentState === "expanded" ? "collapsed" : "expanded"
    ));
  };

  // Crossing the breakpoint decides again; inside it the reader's own choice
  // stands, the way the header's mobile menu follows its own query.
  const followViewport = useCallback((narrow: boolean) => {
    setSidebarState(narrow ? "collapsed" : "expanded");
  }, []);

  useMediaQueryEffect(NARROW_LAYOUT_MEDIA_QUERY, followViewport);

  return (
    <div className={`account-layout account-layout--${sidebarState}`}>
      <AccountSidebar state={sidebarState} onToggle={toggleSidebar} />
      <div className="account-layout__workspace">
        <AccountHeader />
        {session.user?.email_verified === false ? (
          <p className="account-layout__email-notice" role="status">
            {t("auth.emailNotVerified")}
          </p>
        ) : null}
        <div
          className={[
            "account-layout__body",
            isInterfaceBuilderMode
              ? "account-layout__body--interface-builder"
              : "",
          ].filter(Boolean).join(" ")}
        >
          {children}
        </div>
      </div>
      {isInterfaceBuilderMode && isInterfaceBuilderInspectorOpen ? (
        <InterfaceBuilderInspector />
      ) : null}
      {isInterfaceBuilderMode ? <InterfaceBuilderToolbar /> : null}
    </div>
  );
}
